import React, { useMemo, useState } from "react";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import type { ThermalModelConfig } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import { AXES, SHAFT_LEN, HEAD_LEN, HEAD_RAD, SHAFT_RAD, FaceId, FACE_CONFIGS, tempToHex } from "../../constants/thermalConstants";
import { useVertexColors, useCellPositions, useGridLinesGeo } from "../../hooks/CubeAndFaceHooks";
import ThermalSubsystemRenderer from "./ThermalSubsystemRenderer";

// ---------------------------------------------------------------------------
// Cube wireframe + axes
// ---------------------------------------------------------------------------

export const CubeWireframe: React.FC = () => {
    const geo = useMemo(() => new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)), []);
    return (
        <lineSegments geometry={geo}>
            <lineBasicMaterial color="#999999" transparent opacity={0.5} />
        </lineSegments>
    );
};

// Memoized axis geometries to avoid recreating them every render
const cylinderGeo = new THREE.CylinderGeometry(SHAFT_RAD, SHAFT_RAD, SHAFT_LEN, 8);
const coneGeo = new THREE.ConeGeometry(HEAD_RAD, HEAD_LEN, 8);

export interface CubeAxesProps {
    showNadir?: boolean;
    showRollPitchYaw?: boolean;
}

export const CubeAxes: React.FC<CubeAxesProps> = ({ showNadir = true, showRollPitchYaw = true }) => {
    const axisRenderData = useMemo(() => AXES
        .filter(({ label }) => {
            if (label === "X") return showNadir || showRollPitchYaw;
            return showRollPitchYaw;
        })
        .map(({ dir, color, label, annotation }) => {
            const isNadirOnly = label === "X" && showNadir && !showRollPitchYaw;
            const [dx, dy, dz] = dir;
            const conePosP:  [number, number, number] = [dx * (SHAFT_LEN / 2 + HEAD_LEN / 2),  dy * (SHAFT_LEN / 2 + HEAD_LEN / 2),  dz * (SHAFT_LEN / 2 + HEAD_LEN / 2)];
            const labelPosP: [number, number, number] = [dx * (SHAFT_LEN / 2 + HEAD_LEN + 0.07), dy * (SHAFT_LEN / 2 + HEAD_LEN + 0.07), dz * (SHAFT_LEN / 2 + HEAD_LEN + 0.07)];
            const conePosN:  [number, number, number] = [-dx * (SHAFT_LEN / 2 + HEAD_LEN / 2), -dy * (SHAFT_LEN / 2 + HEAD_LEN / 2), -dz * (SHAFT_LEN / 2 + HEAD_LEN / 2)];
            const labelPosN: [number, number, number] = [-dx * (SHAFT_LEN / 2 + HEAD_LEN + 0.07), -dy * (SHAFT_LEN / 2 + HEAD_LEN + 0.07), -dz * (SHAFT_LEN / 2 + HEAD_LEN + 0.07)];
            const quatP = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(dx, dy, dz));
            const rotP  = new THREE.Euler().setFromQuaternion(quatP);
            const rotationP: [number, number, number] = [rotP.x, rotP.y, rotP.z];
            const quatN = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(-dx, -dy, -dz));
            const rotN  = new THREE.Euler().setFromQuaternion(quatN);
            const rotationN: [number, number, number] = [rotN.x, rotN.y, rotN.z];
            const labelStyle: React.CSSProperties = { color, fontSize: 10, fontWeight: 700, fontFamily: "Montserrat, sans-serif", userSelect: "none", pointerEvents: "none" };
            return { label, color, annotation, isNadirOnly, conePosP, labelPosP, conePosN, labelPosN, rotationP, rotationN, labelStyle };
        }), [showNadir, showRollPitchYaw]);

    return (
        <group>
            {axisRenderData.map(({ label, color, isNadirOnly, conePosP, labelPosP, conePosN, labelPosN, rotationP, rotationN, labelStyle, annotation }) => (
                <group key={label}>
                    <mesh geometry={cylinderGeo} rotation={rotationP}>
                        <meshBasicMaterial color={color} />
                    </mesh>
                    <mesh geometry={coneGeo} position={conePosP} rotation={rotationP}>
                        <meshBasicMaterial color={color} />
                    </mesh>
                    <Html center position={labelPosP} zIndexRange={[10, 0]}>
                        {isNadirOnly
                            ? <span style={labelStyle}>Nadir</span>
                            : <span style={labelStyle}>+{label}<span style={{ fontSize: 7, fontWeight: 400, marginLeft: 2 }}>({annotation})</span></span>
                        }
                    </Html>
                    <mesh geometry={coneGeo} position={conePosN} rotation={rotationN}>
                        <meshBasicMaterial color={color} />
                    </mesh>
                    <Html center position={labelPosN} zIndexRange={[10, 0]}>
                        <span style={isNadirOnly ? { ...labelStyle, fontSize: 7, whiteSpace: "nowrap" } : labelStyle}>{isNadirOnly ? "Sun Pointing" : `−${label}`}</span>
                    </Html>
                </group>
            ))}
        </group>
    );
};

// ---------------------------------------------------------------------------
// Face cells + blended face with hover node temperatures
// ---------------------------------------------------------------------------

export interface FaceCellsProps {
    faceId: FaceId;
    nodes: Record<string, number>;
    nodesPerFace: number;
    globalMin: number;
    globalMax: number;
    showNodeTemp?: boolean;
}

// Ghost variant – light-blue transparent fill when Internal Faces layer is on
export const FaceGhost: React.FC<{ faceId: FaceId }> = ({ faceId }) => {
    const { rotation, offset } = FACE_CONFIGS[faceId];
    const pos: [number, number, number] = [offset[0] * 1.001, offset[1] * 1.001, offset[2] * 1.001];
    return (
        <mesh rotation={rotation} position={pos}>
            <planeGeometry args={[1, 1]} />
            <meshBasicMaterial color="#204D69" transparent opacity={0.08} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
    );
};

// ---------------------------------------------------------------------------
// Shared: hover cells component
// ---------------------------------------------------------------------------
const NodeHoverCells: React.FC<{
    cellPositions: { ni: number; x: number; y: number }[];
    cellSize: number;
    nodes: Record<string, number>;
    keyPrefix: string;
    /** When provided, tooltip reads “{title} N{n}: {temp}°C”; otherwise just “{temp}°C” */
    title?: string;
}> = ({ cellPositions, cellSize, nodes, keyPrefix, title }) => {
    const [hoveredNi, setHoveredNi] = useState<number | null>(null);
    return (
        <>
            {cellPositions.map(({ ni, x, y }) => (
                <mesh
                    key={ni}
                    position={[x, y, 0.003]}
                    onPointerEnter={(e) => { e.stopPropagation(); setHoveredNi(ni); }}
                    onPointerLeave={() => setHoveredNi(null)}
                >
                    <planeGeometry args={[cellSize, cellSize]} />
                    <meshBasicMaterial transparent opacity={0} depthWrite={false} />
                </mesh>
            ))}
            {hoveredNi !== null && (() => {
                const { x, y } = cellPositions[hoveredNi];
                const temp = (nodes[`${keyPrefix}_N${hoveredNi + 1}`] ?? 0).toFixed(2);
                const label = title ? `${title} N${hoveredNi + 1}: ${temp}°C` : `${temp}°C`;
                return (
                    <Html position={[x, y, 0.004]} center style={{ pointerEvents: "none" }}>
                        <div style={{
                            color: "#fff",
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: 9,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            textShadow: "0 0 3px #000, 0 0 3px #000",
                            userSelect: "none",
                            pointerEvents: "none",
                        }}>{label}</div>
                    </Html>
                );
            })()}
        </>
    );
};

// ---------------------------------------------------------------------------
// Internal deck planes (Deck 1 = lower, Deck 2 = upper)
// ---------------------------------------------------------------------------

const INTERNAL_DECK_Y    = 1 / 6;
const INTERNAL_DECK_SIZE = 0.98;
const DECK_ROTATION: [number, number, number] = [-Math.PI / 2, 0, 0];
// Static outline geometry — INTERNAL_DECK_SIZE never changes
const OUTLINE_GEO = new THREE.EdgesGeometry(new THREE.PlaneGeometry(INTERNAL_DECK_SIZE, INTERNAL_DECK_SIZE));

export interface InternalDecksHeatmapProps {
    nodes: Record<string, number>;
    nodesPerFace: number;
    globalMin: number;
    globalMax: number;
    showNodeTemp?: boolean;
    showGrid?: boolean;
    showSubsystems?: boolean;
    thermalModel?: ThermalModelConfig;
}

const InternalDeckPlane: React.FC<{
    deckKey: string;
    label: string;
    nodes: Record<string, number>;
    nodesPerFace: number;
    globalMin: number;
    globalMax: number;
    showNodeTemp?: boolean;
    showGrid?: boolean;
}> = ({ deckKey, label, nodes, nodesPerFace, globalMin, globalMax, showNodeTemp, showGrid }) => {
    const gridSize = Math.round(Math.sqrt(nodesPerFace));
    const cellSize = INTERNAL_DECK_SIZE / gridSize;
    const geometry = useVertexColors(gridSize, INTERNAL_DECK_SIZE, nodes, deckKey, globalMin, globalMax);
    const hasData  = nodes[`${deckKey}_N1`] !== undefined;
    const cellPositions = useCellPositions(nodesPerFace, gridSize, cellSize);
    const innerGridGeo  = useGridLinesGeo(gridSize, INTERNAL_DECK_SIZE, false);

    return (
        <>
            {hasData ? (
                <mesh geometry={geometry}>
                    <meshBasicMaterial vertexColors transparent opacity={1} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
                </mesh>
            ) : (
                <mesh>
                    <planeGeometry args={[INTERNAL_DECK_SIZE, INTERNAL_DECK_SIZE]} />
                    <meshBasicMaterial color="#4B84A9" transparent opacity={0.22} side={THREE.DoubleSide} depthWrite={false} />
                </mesh>
            )}
            <lineSegments geometry={OUTLINE_GEO}>
                <lineBasicMaterial color="#8BD0FC" transparent opacity={0.25} />
            </lineSegments>
            {showGrid && innerGridGeo && (
                <lineSegments geometry={innerGridGeo}>
                    <lineBasicMaterial color="#000000" transparent opacity={0.3} depthWrite={false} />
                </lineSegments>
            )}
            {showNodeTemp && hasData && (
                <NodeHoverCells cellPositions={cellPositions} cellSize={cellSize} nodes={nodes} keyPrefix={deckKey} title={label} />
            )}
        </>
    );
};

export const InternalDecksHeatmap: React.FC<InternalDecksHeatmapProps> = React.memo(({ 
    nodes, 
    nodesPerFace, 
    globalMin, 
    globalMax, 
    showNodeTemp, 
    showGrid,
    showSubsystems = false,
    thermalModel,
}) => {
    return (
        <group>
            {/* Internal Deck 1 (lower) */}
            <group position={[0, -INTERNAL_DECK_Y, 0]} rotation={DECK_ROTATION}>
                <InternalDeckPlane deckKey="INT1" label="Deck 1" nodes={nodes} nodesPerFace={nodesPerFace} globalMin={globalMin} globalMax={globalMax} showNodeTemp={showNodeTemp} showGrid={showGrid} />
                {/* Subsystems on Deck 1 */}
                {showSubsystems && (
                    <ThermalSubsystemRenderer
                        deckKey="INT1"
                        currentRow={nodes}
                        globalMin={globalMin}
                        globalMax={globalMax}
                        thermalModel={thermalModel}
                    />
                )}
            </group>
            
            {/* Internal Deck 2 (upper) */}
            <group position={[0, INTERNAL_DECK_Y, 0]} rotation={DECK_ROTATION}>
                <InternalDeckPlane deckKey="INT2" label="Deck 2" nodes={nodes} nodesPerFace={nodesPerFace} globalMin={globalMin} globalMax={globalMax} showNodeTemp={showNodeTemp} showGrid={showGrid} />
                {/* Subsystems on Deck 2 */}
                {showSubsystems && (
                    <ThermalSubsystemRenderer
                        deckKey="INT2"
                        currentRow={nodes}
                        globalMin={globalMin}
                        globalMax={globalMax}
                        thermalModel={thermalModel}
                    />
                )}
            </group>
            
            {/* Bottom Face (+X) and Top Face (-X) */}
            <FaceBlend faceId="PX" nodes={nodes} nodesPerFace={nodesPerFace} globalMin={globalMin} globalMax={globalMax} showNodeTemp={showNodeTemp} />
            <FaceBlend faceId="NX" nodes={nodes} nodesPerFace={nodesPerFace} globalMin={globalMin} globalMax={globalMax} showNodeTemp={showNodeTemp} />
            
            {/* Subsystems on +X face (deck-0) */}
            {showSubsystems && (
                <group position={[0, -0.5, 0]} rotation={[Math.PI / 2, 0, 0]}>
                    <ThermalSubsystemRenderer
                        deckKey="PX"
                        currentRow={nodes}
                        globalMin={globalMin}
                        globalMax={globalMax}
                        thermalModel={thermalModel}
                    />
                </group>
            )}
            
            {/* Subsystems on -X face (deck-3) */}
            {showSubsystems && (
                <group position={[0, 0.5, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                    <ThermalSubsystemRenderer
                        deckKey="NX"
                        currentRow={nodes}
                        globalMin={globalMin}
                        globalMax={globalMax}
                        thermalModel={thermalModel}
                    />
                </group>
            )}
            
            {showGrid && (
                <>
                    <FaceGrid faceId="PX" nodesPerFace={nodesPerFace} />
                    <FaceGrid faceId="NX" nodesPerFace={nodesPerFace} />
                </>
            )}
        </group>
    );
});

// ---------------------------------------------------------------------------
/** Black 3×3 (or n×n) grid lines drawn on a face, matching the thermal-designer cube grid. */
export const FaceGrid: React.FC<{ faceId: FaceId; nodesPerFace: number }> = ({ faceId, nodesPerFace }) => {
    const gridSize = Math.round(Math.sqrt(nodesPerFace));
    const { rotation, offset } = FACE_CONFIGS[faceId];
    const pos: [number, number, number] = [offset[0] * 1.003, offset[1] * 1.003, offset[2] * 1.003];
    const geo = useGridLinesGeo(gridSize, 1, true);

    if (!geo) return null;
    return (
        <group rotation={rotation} position={pos}>
            {/* Render grid on both sides with slight z-offset */}
            <lineSegments geometry={geo} position={[0, 0, 0.001]}>
                <lineBasicMaterial color="#000000" transparent opacity={0.2} depthWrite={false} />
            </lineSegments>
            <lineSegments geometry={geo} position={[0, 0, -0.001]}>
                <lineBasicMaterial color="#000000" transparent opacity={0.2} depthWrite={false} />
            </lineSegments>
        </group>
    );
};

// FaceBlend — vertex-coloured face with per-node hover temperatures
// ---------------------------------------------------------------------------

export const FaceBlend: React.FC<FaceCellsProps> = React.memo(({ faceId, nodes, nodesPerFace, globalMin, globalMax, showNodeTemp }) => {
    const gridSize = Math.round(Math.sqrt(nodesPerFace));
    const cellSize = 1 / gridSize;
    const { rotation, offset } = FACE_CONFIGS[faceId];
    const pos: [number, number, number] = [offset[0] * 1.001, offset[1] * 1.001, offset[2] * 1.001];
    const geometry = useVertexColors(gridSize, 1, nodes, faceId, globalMin, globalMax);
    const cellPositions = useCellPositions(nodesPerFace, gridSize, cellSize);

    // Flip Y coordinate for external faces to match internal deck layout (opposite rotations)
    const flippedCellPositions = useMemo(() => {
        if (!showNodeTemp) return cellPositions;
        return (faceId === "PX" || faceId === "NX") ? cellPositions.map(p => ({ ...p, y: -p.y })) : cellPositions;
    }, [faceId, cellPositions, showNodeTemp]);

    return (
        <group rotation={rotation} position={pos}>
            <mesh geometry={geometry}>
                <meshBasicMaterial vertexColors side={THREE.DoubleSide} toneMapped={false} transparent depthWrite={false} opacity={1} />
            </mesh>
            {showNodeTemp && (
                <NodeHoverCells cellPositions={flippedCellPositions} cellSize={cellSize} nodes={nodes} keyPrefix={faceId} />
            )}
        </group>
    );
});

const DECK_LABELS = [
    { label: "Deck 0", y: -0.5 },
    { label: "Deck 1", y: -INTERNAL_DECK_Y },
    { label: "Deck 2", y: INTERNAL_DECK_Y },
    { label: "Deck 3", y: 0.5 },
];

const DECK_LABEL_STYLE: React.CSSProperties = {
    color: "#f0f0f0",
    fontSize: 7,
    fontWeight: 700,
    fontFamily: "Montserrat, sans-serif",
    userSelect: "none",
    pointerEvents: "none",
    textShadow: "0 0 2px #000",
    whiteSpace: "nowrap",
};

export const DeckLabels: React.FC = () => {
    const xOffset = 0.65;

    return (
        <group>
            {DECK_LABELS.map(({ label, y }) => (
                <Html key={label} position={[xOffset, y, 0]} center style={{ pointerEvents: "none" }}>
                    <div style={DECK_LABEL_STYLE}>{label}</div>
                </Html>
            ))}
        </group>
    );
};
